// A FEJLESZTÉSEK KÉT NÉZETÉNEK tiszta logikája: a board oszlopai és az idővonal idő-csoportjai.
//
// MIÉRT KÖZÖS MODUL: ugyanez a két nézet kell a BELSŐ lapon és a PARTNER-PORTÁLON is, csak más
// oszlop-kulccsal (belül a `stage` — a folyamat —, kívül a `horizon` — a Most/Következik/Később). A
// csoportosítás szabálya (mi kerül melyik oszlopba, mi esik le a boardról, hol vannak az idő-határok)
// EGY helyen él, különben a két felület ugyanarra az adatra mást mondana.
//
// MIÉRT NEM A `stage` A PARTNERNÉL: a belső fázis-nevek kiszivárogtatják a folyamatot és elvárást szülnek
// („in progress? akkor jövő héten kész"). A nyilvános útiterv bevett formája a Most / Következik / Később —
// épp azért, mert nem ígér dátumot. Ezt az adat már hordozza (`horizon`), nem kell hozzá új mező.

/** A boardon MEGJELENŐ fázisok, balról jobbra — ez a folyamat iránya. */
export const BOARD_STAGES = ['idea', 'planned', 'in_progress', 'shipped'] as const;
export type BoardStage = (typeof BOARD_STAGES)[number];

/**
 * A boardról LEKERÜLŐ fázisok. Nem oszlopot kapnak, hanem egy lábléc-jelzést, ami a szűrőre visz.
 *
 * MIÉRT: egy elvetett ötlet nem az, amin dolgozunk — ma mégis az útitervben ült, mert nem `shipped`. Két
 * holt oszlop a képernyő jobb szélén ugyanilyen rossz válasz lett volna: soha senki nem görget odáig.
 */
export const OFF_BOARD_STAGES = ['on_hold', 'cancelled'] as const;

/** Boardon-kívüli fázis-e (szünetel / elvetve) — nincs rá állandó oszlop. */
export function isOffBoardStage(stage: string | null | undefined): boolean {
  return (
    stage !== null && stage !== undefined && (OFF_BOARD_STAGES as readonly string[]).includes(stage)
  );
}

/**
 * A board oszlop-kulcsai a JELENLEGI fázis-szűrő mellett.
 *
 * MIÉRT NEM ELÉG A FIX NÉGY OSZLOP (mért hiba): a szűrő hat fázist kínál, a board viszont csak négyet tud
 * megjeleníteni — „szünetel"-re vagy „elvetve"-re szűrve MINDEN visszakapott sor kiesett az oszlopokból, és
 * a felhasználó NÉGY ÜRES OSZLOPOT látott. A sorok megérkeztek a szerverről, csak nem volt hova tenni őket;
 * ráadásul a board saját lábléc-linkje vitt pont ebbe az állapotba. Egy szűrő-értéknek, amit felkínálunk,
 * MINDIG kell hogy legyen megjelenítése — ez a szabály, nem stílus-kérdés.
 *
 * A megoldás a „nincs holt oszlop" elvet MEGTARTJA: alapértelmezés szerint a folyamat négy oszlopa áll a
 * lapon, és a boardon-kívüli fázis CSAK AKKOR kap oszlopot, ha a felhasználó KIFEJEZETTEN arra szűrt —
 * ilyenkor viszont egyedül áll, mert a másik négy üres oszlop ott már csak zaj („a szüneteltetetteket
 * kérted, tessék").
 */
export function boardColumnsFor(stageFilter: string | null | undefined): readonly string[] {
  return isOffBoardStage(stageFilter) ? [stageFilter!] : BOARD_STAGES;
}

/** A partner-board oszlopai — időhorizont, SOHA nem belső fázis. */
export const HORIZON_COLUMNS = ['now', 'next', 'later'] as const;
export type HorizonColumn = (typeof HORIZON_COLUMNS)[number];

/** Amit a csoportosításhoz tudni kell egy tételről (a belső és a partner-sor közös metszete). */
export interface GroupableItem {
  stage?: string | null;
  horizon?: string | null;
  shippedAt?: string | null;
  publishedAt?: string | null;
}

/** Egy board-oszlop: a kulcs + a bele eső tételek (a bemenő SORREND megtartva — az kurátori). */
export interface BoardColumn<T> {
  key: string;
  items: T[];
}

/**
 * Board-oszlopok egy kulcs-készlet szerint. A bemenő sorrendet NEM rendezi újra: a szerver már kurátori
 * sorrendben adta (horizont → pozíció → cím), és az a sorrend maga az információ.
 *
 * Az ismeretlen (vagy hiányzó) kulcsú tétel KIMARAD az oszlopokból — nem szemetel bele egy „egyéb" oszlopba,
 * mert az a boardon zaj. NEM vész el viszont: az `unkeyedItems()` adja vissza őket a hívónak.
 */
export function toBoardColumns<T extends GroupableItem>(
  items: readonly T[],
  keys: readonly string[],
  keyOf: (item: T) => string | null | undefined,
): BoardColumn<T>[] {
  const byKey = new Map<string, T[]>(keys.map((k) => [k, []]));
  for (const item of items) {
    const key = keyOf(item);
    if (key === null || key === undefined) continue;
    byKey.get(key)?.push(item);
  }
  return keys.map((key) => ({ key, items: byKey.get(key) ?? [] }));
}

/**
 * Amit a board oszlopai NEM fogadnak be: nincs kulcsa, vagy ismeretlen a kulcsa.
 *
 * MIÉRT KELL: a partner-boardon az oszlop-kulcs a `horizon`, ami NULLÁZHATÓ — egy kihirdetett, publikus
 * tétel horizont nélkül némán eltűnt volna a lapról (élő e2e-n mérve). Amit a partnernek megígértünk, azt
 * meg is mutatjuk: a hívó ezeket NEGYEDIK, kimondott OSZLOPKÉNT teszi a három horizont mellé
 * (`portal.product-updates.tsx`) — ugyanazzal az elvvel, amivel az idővonal a dátum nélküli tételt kezeli.
 *
 * A belső boardon ez üres marad (a `stage` kötelező enum, és mind a hat értékének van helye).
 */
export function unkeyedItems<T extends GroupableItem>(
  items: readonly T[],
  keys: readonly string[],
  keyOf: (item: T) => string | null | undefined,
): T[] {
  const known = new Set(keys);
  return items.filter((item) => {
    const key = keyOf(item);
    return key === null || key === undefined || !known.has(key);
  });
}

/** A boardról lekerülő (függő/elvetett) tételek — a lábléc-jelzéshez. */
export function offBoardItems<T extends GroupableItem>(items: readonly T[]): T[] {
  const off = new Set<string>(OFF_BOARD_STAGES);
  return items.filter((i) =>
    i.stage === null || i.stage === undefined ? false : off.has(i.stage),
  );
}

/** Egy idő-csoport az idővonalon: a hónap-kulcs (`YYYY-MM`) + a bele eső tételek. */
export interface TimelineGroup<T> {
  /** `YYYY-MM`, vagy `''` a dátum nélkülieknek (azok a lista VÉGÉRE kerülnek). */
  month: string;
  items: T[];
}

/** Egy tétel idő-bélyege az idővonalhoz: a KIADÁS dátuma, tartalékban a kihirdetésé. */
export function timelineDay(item: GroupableItem): string {
  return (item.shippedAt ?? item.publishedAt ?? '').slice(0, 10);
}

/**
 * Idővonal-csoportok HÓNAP szerint, a legfrissebb elöl — ez a változásnapló bevett alakja, és a
 * csoport-fejléc az, ami a görgetés közben elhelyez az időben.
 *
 * A dátum NÉLKÜLI tételek nem vesznek el: egy üres kulcsú csoportba kerülnek a lista VÉGÉRE (a felület
 * „ismeretlen időpont"-ként írja ki). Némán eldobni őket rosszabb lenne, mint bevallani, hogy nincs dátum.
 */
export function toTimelineGroups<T extends GroupableItem>(items: readonly T[]): TimelineGroup<T>[] {
  const byMonth = new Map<string, T[]>();
  for (const item of items) {
    const month = timelineDay(item).slice(0, 7);
    (byMonth.get(month) ?? byMonth.set(month, []).get(month)!).push(item);
  }
  return [...byMonth.entries()]
    .sort(([a], [b]) => {
      if (a === b) return 0;
      if (a === '') return 1; // a dátum nélküliek a végére
      if (b === '') return -1;
      return b.localeCompare(a); // legfrissebb elöl
    })
    .map(([month, group]) => ({ month, items: group }));
}
